import React from 'react'
import type { Review } from '@homeviews/models'
import { StarRating, StatusLabel } from '@homeviews/shared-ui'
import Skeleton from '@material-ui/lab/Skeleton'
import { REVIEWER_TYPES } from '@homeviews/statistics'
import { formatDateDefault } from '@homeviews/utils'

interface HeadCell {
  disablePadding: boolean
  id: string
  label: string
  numeric: boolean
  sortable?: boolean
  width?: number
}

export const headCells: HeadCell[] = [
  {
    id: 'verified',
    numeric: false,
    disablePadding: false,
    label: 'Verified',
    sortable: false,
    width: 70,
  },
  {
    id: 'title',
    numeric: false,
    disablePadding: false,
    label: 'Review Title',
    sortable: true,
    width: 350,
  },
  {
    id: 'display_name',
    numeric: false,
    disablePadding: false,
    label: 'Display Name',
    sortable: false,
    width: 180,
  },
  {
    id: 'rating',
    numeric: true,
    disablePadding: false,
    label: 'Overall Score',
    sortable: true,
    width: 135,
  },
  {
    id: 'reviewer_type',
    numeric: true,
    disablePadding: false,
    label: 'Type',
    width: 140,
  },
  {
    id: 'development_name',
    numeric: true,
    disablePadding: false,
    label: 'Development',
    sortable: true,
    width: 300,
  },
  {
    id: 'rating_facilities',
    numeric: true,
    disablePadding: false,
    label: 'Facilities',
    sortable: true,
    width: 90,
  },
  {
    id: 'rating_design',
    numeric: true,
    disablePadding: false,
    label: 'Design',
    sortable: true,
    width: 82,
  },
  {
    id: 'rating_location',
    numeric: true,
    disablePadding: false,
    label: 'Location',
    sortable: true,
    width: 90,
  },
  {
    id: 'rating_value',
    numeric: true,
    disablePadding: false,
    label: 'Value',
    sortable: true,
    width: 75,
  },
  {
    id: 'rating_management',
    numeric: true,
    disablePadding: false,
    label: 'Management',
    sortable: true,
    width: 120,
  },
  {
    id: 'date',
    numeric: true,
    disablePadding: false,
    label: 'Date',
    sortable: true,
    width: 120,
  },
  {
    id: 'rating_status',
    numeric: true,
    disablePadding: false,
    label: 'Status',
    width: 150,
  },
]

function createData({
  id,
  title,
  display_name,
  rating,
  reviewer_type,
  development_name,
  rating_facilities,
  rating_design,
  rating_location,
  rating_value,
  rating_management,
  status,
  review_date,
  verified,
}: Review) {
  return [
    id,
    <span key="verified" className="c-reviewsTable__verifiedCell">
      <img
        src={`assets/icons/small/${
          verified ? 'verified' : 'unverified_v2'
        }.svg`}
        alt={`Verified review icon`}
        style={{ width: '24px' }}
      />
    </span>,
    <span key="title" className="c-reviewsTable__reviewBodyCell">
      {title}
    </span>,
    <span key="name" className="c-reviewsTable__reviewBodyCell">
      {display_name}
    </span>,
    <StarRating key="rating" value={rating} fontSize={16} />,
    <React.Fragment key="reviewer-type">
      {REVIEWER_TYPES?.[reviewer_type]?.label || reviewer_type}
    </React.Fragment>,
    <React.Fragment key="dev-name">{development_name}</React.Fragment>,
    <React.Fragment key="facilities">{rating_facilities}</React.Fragment>,
    <React.Fragment key="design">{rating_design}</React.Fragment>,
    <React.Fragment key="location">{rating_location}</React.Fragment>,
    <React.Fragment key="value">{rating_value}</React.Fragment>,
    <React.Fragment key="management">{rating_management}</React.Fragment>,
    <React.Fragment key="date">
      {formatDateDefault(review_date)}
    </React.Fragment>,
    <StatusLabel key="status" status={status} />,
  ]
}

export const createRows = (reviews: Review[]) => {
  if (!reviews.length) {
    return []
  }
  return reviews.map((review: Review) => createData(review))
}

export const createSkeletonRows = () =>
  Array(20)
    .fill('0')
    .map((_, idx) =>
      Array(12)
        .fill('0')
        .map((_, index) => {
          return <Skeleton key={`${idx}-${index}`} />
        }),
    )
